import _get from 'lodash/get';
import { getPaxUserInfo, isPaxUser } from './grab-id-auth-helper';

/**
 * Get PaySDK SDK URL from config.
 * Single URL per env—dev uses this value; stg/prd values are set in sysops/deploy.
 */
export const getPaySdkWebSdkUrl = () => {
  const url = _get(window.Grab, 'config.paysdkWebSdkUrl', '');
  return typeof url === 'string' ? url : '';
};

/**
 * Load PaySDK script dynamically
 * @returns {Promise<void>} Resolves when PaySDK is available on window
 */
export const loadPaySdkScript = () => {
  const sdkUrl = getPaySdkWebSdkUrl();
  if (!sdkUrl) {
    return Promise.reject(new Error('PaySDK URL not configured'));
  }

  if (typeof window !== 'undefined' && window.PaySDK) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${sdkUrl}"]`);
    if (existingScript) {
      const checkPaySdk = () => {
        if (window.PaySDK) {
          resolve();
        } else {
          setTimeout(checkPaySdk, 50);
        }
      };
      checkPaySdk();
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = sdkUrl;
    script.defer = true;
    script.onload = () => {
      if (window.PaySDK) {
        resolve();
      } else {
        reject(new Error('PaySDK script loaded but not available on window'));
      }
    };
    script.onerror = () =>
      reject(new Error(`Failed to load PaySDK script from ${sdkUrl}`));
    document.head.appendChild(script);
  });
};

/**
 * Get SDK context for PaySDK initialization.
 * Pax: userId from getPaxUserInfo (sub or safeId).
 * GFB: userId from userInfo.user.safeID (from Redux, set by portal/cookie auth).
 * Uses Method 2 (automatic cookie auth) - no token passed.
 */
export const getPaySdkContext = userInfo => {
  // GFB or Pax with userInfo from Redux
  const safeIDFromUserInfo = _get(userInfo, 'user.safeID');
  if (safeIDFromUserInfo) {
    return { userId: safeIDFromUserInfo };
  }
  // Pax only - from GrabID storage
  if (isPaxUser()) {
    const paxUserInfo = getPaxUserInfo() || {};
    const userId =
      _get(paxUserInfo, 'sub') || _get(paxUserInfo, 'safeId') || '';
    return { userId };
  }
  return { userId: '' };
};
